import { FC, memo } from "react";

import {
  PinFilled,
  UserAvatar,
  OverflowMenuVertical,
  DotMark,
  Star,
  Building,
  Group,
} from "@carbon/icons-react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import { GroupPathsEnum } from "@/features/groups/constants/group.paths";
import { useAppDispatch } from "@/redux/store";
import formatNumber from "@/utils/formatNumber";
import { getMessage } from "@/utils/message";

import JoinButton from "./JoinButton";
import { deleteGroup } from "../../api/group.api";
import { groupActions } from "../../group";
import { GroupDef } from "../../types/group.type";

interface GroupCardProps {
  group: GroupDef;
  type?: "view" | "invite" | "request" | "deleted" | "discover" | "suggest";
  onAccept?: (groupId: number, type: string | null) => void;
  onReject?: (groupId: number, type: string | null) => void;
  onCancel?: (requestId: string, userId: number) => void;
  onRestore?: (groupId: number) => void;
}

const GroupCard: FC<GroupCardProps> = ({ group, type = "view" }) => {
  const { toast } = useToast();
  const dispatch = useAppDispatch();

  const handleDeleteGroup = async () => {
    try {
      if (group) {
        dispatch(groupActions.setFlagLoading(true));
        await deleteGroup(group.groupId);
        toast({
          title: "Thành công!",
          variant: "success",
          description: getMessage("MSG5", group.name),
        });
      }
    } catch (error: any) {
      toast({
        title: "Thất bại!",
        variant: "error",
        description: getMessage(error.response?.data.errors[0]),
      });
    } finally {
      dispatch(groupActions.setFlagLoading(false));
    }
  };

  // Get role info based on role code
  const getRoleInfo = () => {
    if (!group.role) return null;

    switch (group.role.code) {
      case "OWNER":
        return {
          label: "Sở hữu",
          bgClass: "bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700",
          icon: (
            <Star
              size={14}
              className="mr-1.5"
            />
          ),
        };
      case "ADMIN":
        return {
          label: "Quản trị",
          bgClass:
            "bg-gradient-to-r from-purple-50 to-indigo-50 text-purple-700",
          icon: (
            <Building
              size={14}
              className="mr-1.5"
            />
          ),
        };
      default:
        return {
          label: "Thành viên",
          bgClass: "bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700",
          icon: (
            <Group
              size={14}
              className="mr-1.5"
            />
          ),
        };
    }
  };

  const roleInfo = getRoleInfo();

  return (
    <div className="group relative bg-white rounded-2xl border border-secondary-200 hover:border-primary-300 hover:shadow-xl transition-all duration-300 ease-out overflow-hidden">
      {/* Cover Image with Overlay */}
      <div className="relative h-48 overflow-hidden bg-gradient-to-br from-primary-50 to-primary-100">
        {group.photoUrl ? (
          <img
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            src={group.photoUrl}
            alt={group.name}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-100 via-primary-50 to-secondary-50">
            <div className="text-center">
              {group.isRootGroup ? (
                <Building
                  size={48}
                  className="text-primary-400 mx-auto mb-2"
                />
              ) : (
                <Group
                  size={48}
                  className="text-primary-400 mx-auto mb-2"
                />
              )}
              <span className="text-sm font-medium text-primary-600">
                {group.isRootGroup ? "Tổ chức" : "Nhóm"}
              </span>
            </div>
          </div>
        )}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent"></div>

        {/* Group Type Badge */}
        <div className="absolute top-4 left-4">
          <Badge
            className={`
            px-2 py-1 text-xs font-medium rounded-full border-0 backdrop-blur-sm
            ${
              group.isRootGroup
                ? "bg-white/90 text-primary-700"
                : "bg-white/90 text-secondary-700"
            }
          `}
          >
            {group.isRootGroup ? (
              <>
                <Building
                  size={12}
                  className="mr-1"
                />
                Tổ chức
              </>
            ) : (
              <>
                <Group
                  size={12}
                  className="mr-1"
                />
                Nhóm
              </>
            )}
          </Badge>
        </div>

        {/* Pin Badge */}
        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-sm">
          <PinFilled
            className="text-primary-600"
            size={16}
          />
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Header with Category */}
        <div className="mb-1">
          {/* Parent Organization */}
          {group.rootName && (
            <div className="flex items-center text-xs font-medium text-secondary-500 uppercase tracking-wider mb-1">
              <span>Tổ chức</span>
              <DotMark
                size={4}
                className="mx-2"
              />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      className="text-primary-600 hover:text-primary-700 transition-colors duration-200 normal-case max-w-[180px] truncate"
                      to={GroupPathsEnum.GROUP_DETAIL.replace(
                        ":id",
                        String(group.rootId)
                      )}
                    >
                      {group.rootName}
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent>
                    <span className="text-xs">{group.rootName}</span>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}

          {/* Group Title */}
          <h3 className="mb-3">
            <Link
              className="text-xl font-bold text-secondary-900 hover:text-primary-600 transition-colors duration-200 line-clamp-2 leading-tight"
              to={GroupPathsEnum.GROUP_DETAIL.replace(
                ":id",
                String(group.groupId)
              )}
            >
              {group.name}
            </Link>
          </h3>
        </div>

        {/* Stats Row */}
        <div className="flex items-center justify-between mb-4">
          {/* Role Badge */}
          {type === "view" && roleInfo && (
            <Badge
              className={`px-3 py-1.5 text-xs font-medium rounded-full border-0 ${roleInfo.bgClass}`}
            >
              {roleInfo.icon}
              {roleInfo.label}
            </Badge>
          )}

          {/* Members Count */}
          <div className="flex items-center text-sm text-secondary-600 bg-secondary-50 px-3 py-1.5 rounded-full">
            <UserAvatar
              size={16}
              className="mr-2 text-secondary-500"
            />
            <span className="font-medium">
              {formatNumber(group.numberOfMembers)}
            </span>
            <span className="ml-1 text-secondary-500">thành viên</span>
          </div>
        </div>

        {/* Actions */}
        {type === "view" && (
          <div className="flex items-center gap-3">
            <Link
              className="flex-1 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-medium text-sm px-4 py-3 rounded text-center transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
              to={GroupPathsEnum.GROUP_DETAIL.replace(
                ":id",
                String(group.groupId)
              )}
            >
              Xem chi tiết
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  className="w-12 h-12 rounded border border-secondary-200 hover:border-secondary-300 hover:bg-secondary-50 transition-all duration-200 hover:-translate-y-0.5"
                  variant="ghost"
                  size="sm"
                >
                  <OverflowMenuVertical
                    size={18}
                    className="text-secondary-600"
                  />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-52"
                align="end"
              >
                <DropdownMenuItem className="py-3 cursor-pointer hover:bg-secondary-50 transition-colors">
                  <span className="text-sm">Ghim nhóm</span>
                </DropdownMenuItem>
                <DropdownMenuItem className="py-3 cursor-pointer hover:bg-secondary-50 transition-colors">
                  <span className="text-sm">Bỏ ghim nhóm</span>
                </DropdownMenuItem>
                {group.isRootGroup &&
                  (group.role?.code === "OWNER" ||
                    group.role?.code === "ADMIN") && (
                    <DropdownMenuItem
                      className="py-3 cursor-pointer text-red-600 hover:bg-red-50 transition-colors"
                      onClick={() => handleDeleteGroup()}
                    >
                      <span className="text-sm">Xóa tổ chức</span>
                    </DropdownMenuItem>
                  )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {type === "discover" && (
          <div className="mt-6">
            <JoinButton
              status="discover"
              group={group}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(GroupCard);
